type PrivacyPanelProps = {
  isOpen: boolean
  onClose: () => void
}

export function PrivacyPanel({ isOpen, onClose }: PrivacyPanelProps) {
  const policyLastUpdated = '2026-06-08'

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      role="dialog"
      aria-modal="true"
      aria-label="Privacy settings"
    >
      <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-[1.75rem] border border-edge/70 bg-surface p-6 shadow-panel sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="eyebrow text-accent">NEENO Lab</p>
            <h2 className="mt-1.5 text-lg font-medium tracking-tight text-primary sm:text-xl">
              Privacy Policy & Legal Notice
            </h2>
            <p className="mt-1 text-sm text-muted">
              This panel contains the Privacy Policy and Legal Notice for NEENO Lab.
            </p>
          </div>
          <button type="button" onClick={onClose} className="pill shrink-0">
            Close
          </button>
        </div>

        <section className="mt-8 space-y-3">
          <h3 className="text-sm font-semibold tracking-tight text-primary">Privacy Policy</h3>
          <p className="text-sm text-muted">
            This website is a technical showcase for NEENO Lab. Personal data is processed only to
            the extent required for secure operation. Optional analytics data is collected only
            after explicit consent.
          </p>
          <p className="text-sm text-muted">
            Legal bases: GDPR Article 6(1)(f) for secure website operation and GDPR Article 6(1)(a)
            for optional analytics processing based on consent.
          </p>
          <p className="text-sm text-muted">
            Data categories (when analytics consent is granted): pseudonymous usage data, page
            views, browser/device information, and approximate location derived from IP.
          </p>
          <p className="text-sm text-muted">
            Recipients/processors: Google Analytics (Google Ireland Limited). Processing outside the
            EEA may occur depending on provider infrastructure and contractual safeguards.
          </p>
          <p className="text-sm text-muted">
            Data subject rights: access, rectification, erasure, restriction, portability,
            objection, and complaint with a supervisory authority.
          </p>
          <p className="text-xs text-muted">Privacy policy effective date: {policyLastUpdated}</p>
        </section>

        <section className="mt-8 space-y-3">
          <h3 className="text-sm font-semibold tracking-tight text-primary">
            Legal Notice (Impressum)
          </h3>
          <p className="text-sm text-muted">
            Information provided under applicable legal information duties.
          </p>
          <div className="rounded-2xl border border-edge/60 bg-canvas/40 p-4 text-sm text-muted">
            <p className="text-primary">NEENO / NEENO Lab</p>
            <p>
              Website:{' '}
              <a
                href="https://lab.neeno.io"
                target="_blank"
                rel="noreferrer"
                className="text-accent underline underline-offset-2"
              >
                https://lab.neeno.io
              </a>
            </p>
            <p>
              Contact: via the contact channels available on the main site:{' '}
              <a
                href="https://neeno.io"
                target="_blank"
                rel="noreferrer"
                className="text-accent underline underline-offset-2"
              >
                https://neeno.io
              </a>
              .
            </p>
            <p>
              For statutory company details (registered address, commercial register entry, VAT ID),
              please contact us at{' '}
              <a
                href="mailto:contact@myneeno.com"
                className="text-accent underline underline-offset-2"
              >
                contact@myneeno.com
              </a>
            </p>
          </div>
        </section>

        <section className="mt-8 space-y-3">
          <h3 className="text-sm font-semibold tracking-tight text-primary">Copyright</h3>
          <p className="text-sm text-muted">
            Content and works created by NEENO on these pages are subject to German copyright law.
            Duplication, processing, distribution, or any form of commercialization beyond the scope
            of copyright law requires prior written consent of NEENO.
          </p>
        </section>

        <section className="mt-8 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-edge/60 bg-canvas/40 p-4">
          <p className="text-xs text-muted">More details are available on the main website:</p>
          <a
            href="https://neeno.io"
            target="_blank"
            rel="noreferrer"
            className="pill border-primary/30 text-primary hover:border-primary hover:shadow-glow-sm"
          >
            Go to neeno.io
          </a>
        </section>
      </div>
    </div>
  )
}
